(function() {
  'use strict';

  /**
   * Resizes + compresses an image File to a JPEG data URL.
   * Dramatically reduces cart payload size so add/change/update requests stay fast.
   */
  function compressImageFile(file, maxDimension = 1200, quality = 0.8) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          let { width, height } = img;
          if (width > maxDimension || height > maxDimension) {
            const scale = maxDimension / Math.max(width, height);
            width = Math.round(width * scale);
            height = Math.round(height * scale);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  }

  /* Named so it can run again for markup injected after load — the Live In
     Hoops product tabs fetch this section via the Section Rendering API,
     long after DOMContentLoaded has fired. The binding below is unchanged. */
  function initLpPdpGallery() {
    const section = document.querySelector('.lp-pdp-engraved-locket');
    if (!section) return;

    /** Maps variant id -> featured media id (from Liquid); used to match gallery slides and reuse data-main-src URLs. */
    let featuredMediaMap = {};
    const featuredMediaEl = section.querySelector('#lp-variant-featured-media');
    if (featuredMediaEl) {
      try {
        featuredMediaMap = JSON.parse(featuredMediaEl.textContent);
      } catch (e) {
        featuredMediaMap = {};
      }
    }

    // ============ Lightbox Variables ============
    let allImages = [];
    let currentImageIndex = 0;
    /** Slide whose video playback is already synced; -1 after the track is rebuilt. */
    let lastPlayedIndex = -1;

    // ============ Main Image Update Function ============
    /* `let`: when the product's first media is a video, #lp-main-img starts as a <video>
       and is swapped for an <img> the first time a variant image is shown. */
    let mainImage = section.querySelector('#lp-main-img');

    /** Resolves the media id for a variant: Liquid map first, then the variant JSON. */
    function getVariantMediaId(variant) {
      if (!variant) return null;
      if (featuredMediaMap[variant.id] != null) return featuredMediaMap[variant.id];
      if (variant.featured_media && variant.featured_media.id) return variant.featured_media.id;
      return null;
    }

    /**
     * Index of the swiper slide holding `mediaId`, counted across ALL slides. Counting only
     * image slides (as before) skipped video slides and landed on the wrong slide.
     */
    function getSlideIndexForMedia(mediaId) {
      if (mediaId == null) return -1;
      const slides = Array.from(section.querySelectorAll('.lp-pdp__swiper-slide'));
      return slides.findIndex((slide) => slide.querySelector('[data-media-id="' + mediaId + '"]'));
    }

    /**
     * Resolves the main image URL to match #lp-main-img width (1000w) when the slide exists in the gallery,
     * so swaps reuse the same URL we prefetch and avoid an extra CDN fetch for a different resize.
     */
    function getMainImageUrlForVariant(variant) {
      if (!variant) {
        return null;
      }

      const mediaId = getVariantMediaId(variant);

      if (mediaId != null) {
        const slideImg = section.querySelector(
          '.lp-pdp__swiper-slide img[data-media-id="' + mediaId + '"]'
        );
        if (slideImg) {
          const mainSrc = slideImg.getAttribute('data-main-src');
          if (mainSrc) {
            return mainSrc;
          }
        }
      }

      if (variant.featured_image && variant.featured_image.src) {
        return variant.featured_image.src;
      }
      if (
        variant.featured_media &&
        variant.featured_media.preview_image &&
        variant.featured_media.preview_image.src
      ) {
        return variant.featured_media.preview_image.src;
      }
      return null;
    }

    /** Preload main-size images for every gallery slide so the first swatch change hits cache after refresh. */
    function prefetchMainImagesForSwatches() {
      // The main image only shows on desktop; don't spend mobile bandwidth on it.
      if (isMobileGallery()) return;
      const seen = new Set();
      const slideImages = section.querySelectorAll('.lp-pdp__swiper-slide img[data-main-src]');
      for (const img of slideImages) {
        const url = img.getAttribute('data-main-src');
        if (url && !seen.has(url)) {
          seen.add(url);
          const pre = new Image();
          pre.src = url;
        }
      }
    }

    /** @param {string} imageSrc @param {string} [altText] - Alt of the image being shown, when known. */
    function updateMainImage(imageSrc, altText) {
      if (!mainImage || !imageSrc) return;

      // Setting an image URL as a <video> src breaks the video and never shows the image,
      // so replace the hero video with an <img> (and drop its pause control + role="img").
      if (mainImage.tagName === 'VIDEO') {
        const container = section.querySelector('#lp-main-image');
        const img = document.createElement('img');
        img.className = 'lp-pdp__main-img';
        img.id = 'lp-main-img';
        img.alt = (container && container.getAttribute('aria-label')) || '';
        if (container) {
          container.removeAttribute('role');
          container.removeAttribute('aria-label');
          container.querySelector('.ada-video-pause-btn')?.remove();
        }
        mainImage.replaceWith(img);
        mainImage = img;
      }

      mainImage.src = imageSrc;
      if (altText) mainImage.alt = altText;
    }

    // ============ Initialize All Images ============
    function initializeAllImages() {
      const slides = Array.from(section.querySelectorAll('.lp-pdp__swiper-slide'));

      allImages = [];

      slides.forEach(slide => {
        const video = slide.querySelector('video');
        if (video) {
          const source = video.querySelector('source');
          if (source && source.src) {
            allImages.push({
              type: 'video',
              src: source.src,
              poster: video.dataset.previewSrc || video.poster || '',
              // component-video-a11y-sanitize moves Shopify's description here.
              label: video.dataset.videoLabel || '',
            });
          }
          return;
        }

        const img = slide.querySelector('img');
        if (img && img.src) {
          // The lightbox fills the screen, so it uses the large copy (lp-pdp-gallery.liquid), not the slide's 600px one.
          allImages.push({ type: 'image', src: img.dataset.zoomSrc || img.src, label: img.alt || '' });
        }
      });

      // If no media found, try to get from featured image
      if (allImages.length === 0 && mainImage && mainImage.src) {
        allImages.push({ type: 'image', src: mainImage.src, label: mainImage.alt || '' });
      }
    }

    // ============ Lightbox Functions ============
    const lightbox = section.querySelector('#lp-lightbox');
    const lightboxTrack = section.querySelector('#lp-lightbox-track');
    const lightboxClose = section.querySelector('#lp-lightbox-close');
    const lightboxPrev = section.querySelector('#lp-lightbox-prev');
    const lightboxNext = section.querySelector('#lp-lightbox-next');
    const lightboxOverlay = section.querySelector('.lp-pdp__lightbox-overlay');
    const lightboxDotsContainer = section.querySelector('#lp-lightbox-dots');
    /** Localized fallback labels, stamped on #lp-lightbox by lp-pdp-lightbox-modal.liquid. */
    const fallbackImageLabel = lightbox ? lightbox.dataset.fallbackImageLabel || '' : '';
    const fallbackVideoLabel = lightbox ? lightbox.dataset.fallbackVideoLabel || '' : '';

    function createLightboxDots() {
      if (!lightboxDotsContainer) return;
      
      lightboxDotsContainer.innerHTML = '';
      allImages.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.className = 'lp-pdp__lightbox-dot';
        if (index === currentImageIndex) {
          dot.classList.add('lp-pdp__lightbox-dot--active');
        }
        dot.addEventListener('click', (e) => {
          e.stopPropagation();
          currentImageIndex = index;
          if (lightboxTrack) {
            lightboxTrack.style.transition = 'transform 0.3s ease-out';
            lightboxTrack.style.transform = `translate3d(-${currentImageIndex * 100}%, 0, 0)`;
          }
          updateLightboxControls();
        });
        lightboxDotsContainer.appendChild(dot);
      });
    }

    /**
     * One lightbox slide: an <img>, or a <video> plus its description for AT.
     * @param {{ type: string, src: string, label?: string, poster?: string }} media
     * @param {boolean} lazy - Load the image only when the shopper reaches it. Set before `src`, which starts the download.
     * @returns {HTMLImageElement|HTMLDivElement} The image, or a <div> holding the video and its description.
     */
    function createLightboxSlide(media, lazy) {
      if (media.type !== 'video') {
        const img = document.createElement('img');
        img.className = 'lp-pdp__lightbox-image';
        if (lazy) img.loading = 'lazy';
        img.src = media.src;
        img.alt = media.label || fallbackImageLabel;
        return img;
      }

      const video = document.createElement('video');
      video.className = 'lp-pdp__lightbox-image';
      video.src = media.src;
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      if (media.poster) video.poster = media.poster;

      // A <video> cannot carry an accessible name, so it stays hidden from AT and
      // the description is exposed as text instead. The aria-hidden parent is also
      // what tells component-video-pause.js to inject its play/pause control as a
      // sibling rather than inside the hidden subtree — keep the video wrapped so
      // that control lands in the slide and the track keeps one child per slide.
      const videoWrap = document.createElement('div');
      videoWrap.setAttribute('aria-hidden', 'true');
      videoWrap.style.width = '100%';
      videoWrap.style.height = '100%';
      videoWrap.appendChild(video);

      const description = document.createElement('span');
      description.className = 'lp-pdp__visually-hidden';
      description.textContent = media.label || fallbackVideoLabel;

      const slide = document.createElement('div');
      slide.appendChild(videoWrap);
      slide.appendChild(description);
      return slide;
    }

    function createLightboxImages() {
      if (!lightboxTrack) return;
      lightboxTrack.innerHTML = '';
      lastPlayedIndex = -1;
      for (const [index, media] of allImages.entries()) {
        // Only the opened image loads at once; the others load as the shopper reaches them.
        const slide = createLightboxSlide(media, index !== currentImageIndex);
        slide.style.flex = '0 0 100%';
        slide.style.width = '100%';
        slide.style.height = '100%';
        slide.style.objectFit = 'contain';
        slide.draggable = false;
        lightboxTrack.appendChild(slide);
      }
    }

    /**
     * Plays the video on the active slide and pauses every other one. Only runs when
     * the slide actually changes — otherwise it would re-play a video the shopper just
     * paused with the component-video-pause.js control.
     */
    function syncLightboxVideos() {
      if (!lightboxTrack || lastPlayedIndex === currentImageIndex) return;
      lastPlayedIndex = currentImageIndex;
      Array.from(lightboxTrack.children).forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (index === currentImageIndex) {
          const playing = video.play();
          if (playing) playing.catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    function openLightboxAtIndex(index) {
      if (index < 0 || index >= allImages.length) return;
      
      currentImageIndex = index;
      createLightboxImages();
      
      if (lightboxTrack) {
        lightboxTrack.style.transition = 'none';
        lightboxTrack.style.transform = `translate3d(-${currentImageIndex * 100}%, 0, 0)`;
      }

      lightbox.classList.add('lp-pdp__lightbox--active');
      updateLightboxControls();
      createLightboxDots();
    }

    function closeLightbox() {
      lightbox.classList.remove('lp-pdp__lightbox--active');
      if (lightboxTrack) {
        lightboxTrack.querySelectorAll('video').forEach(video => video.pause());
      }
    }

    function updateLightboxControls() {
      // Disable/Enable prev arrow
      if (lightboxPrev) {
        lightboxPrev.disabled = currentImageIndex === 0;
      }
      
      // Disable/Enable next arrow
      if (lightboxNext) {
        lightboxNext.disabled = currentImageIndex === allImages.length - 1;
      }

      // Update active dot
      const dots = lightboxDotsContainer.querySelectorAll('.lp-pdp__lightbox-dot');
      dots.forEach((dot, index) => {
        if (index === currentImageIndex) {
          dot.classList.add('lp-pdp__lightbox-dot--active');
        } else {
          dot.classList.remove('lp-pdp__lightbox-dot--active');
        }
      });

      syncLightboxVideos();
    }

    function nextImage() {
      if (currentImageIndex < allImages.length - 1) {
        currentImageIndex++;
        if (lightboxTrack) {
          lightboxTrack.style.transition = 'transform 0.3s ease-out';
          lightboxTrack.style.transform = `translate3d(-${currentImageIndex * 100}%, 0, 0)`;
        }
        updateLightboxControls();
      }
    }

    function prevImage() {
      if (currentImageIndex > 0) {
        currentImageIndex--;
        if (lightboxTrack) {
          lightboxTrack.style.transition = 'transform 0.3s ease-out';
          lightboxTrack.style.transform = `translate3d(-${currentImageIndex * 100}%, 0, 0)`;
        }
        updateLightboxControls();
      }
    }

    // Lightbox event listeners
    if (lightboxClose) {
      lightboxClose.addEventListener('click', (e) => {
        e.stopPropagation();
        closeLightbox();
      });
    }

    // Close when clicking on overlay or any empty space in lightbox
    if (lightbox) {
      lightbox.addEventListener('click', (e) => {
        // Prevent closing if we click the wrapper/track, arrows, or pagination dots
        if (
          e.target.closest('.lp-pdp__lightbox-image-wrapper') ||
          e.target.closest('#lp-lightbox-track') ||
          e.target.closest('.lp-pdp__lightbox-arrow') ||
          e.target.closest('.lp-pdp__lightbox-pagination')
        ) {
          return;
        }
        
        closeLightbox();
      });
    }

    if (lightboxPrev) {
      lightboxPrev.addEventListener('click', (e) => {
        e.stopPropagation();
        prevImage();
      });
    }

    if (lightboxNext) {
      lightboxNext.addEventListener('click', (e) => {
        e.stopPropagation();
        nextImage();
      });
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!lightbox.classList.contains('lp-pdp__lightbox--active')) return;
      
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      }
    });

    // Touch and Drag (Swipe) navigation
    let touchStartX = 0;
    let touchCurrentX = 0;
    let isDragging = false;
    let trackWidth = 0;

    const setDragPosition = (x) => {
      touchCurrentX = x;
      const diff = touchCurrentX - touchStartX;
      if (lightboxTrack) {
        lightboxTrack.style.transition = 'none';
        
        let newX = -(currentImageIndex * trackWidth) + diff;
        
        if (newX > 0) {
           newX = newX * 0.3; 
        } else if (newX < -(allImages.length - 1) * trackWidth) {
           const overscroll = newX - (-(allImages.length - 1) * trackWidth);
           newX = (-(allImages.length - 1) * trackWidth) + (overscroll * 0.3);
        }

        lightboxTrack.style.transform = `translate3d(${newX}px, 0, 0)`;
      }
    };

    const handleSwipeEnd = () => {
      if (!isDragging) return;
      
      const diff = touchCurrentX - touchStartX;
      const swipeThreshold = 50;

      if (diff < -swipeThreshold && currentImageIndex < allImages.length - 1) {
        currentImageIndex++;
      } else if (diff > swipeThreshold && currentImageIndex > 0) {
        currentImageIndex--;
      }

      if (lightboxTrack) {
        lightboxTrack.style.transition = 'transform 0.3s ease-out';
        lightboxTrack.style.transform = `translate3d(-${currentImageIndex * 100}%, 0, 0)`;
      }
      updateLightboxControls();
      
      isDragging = false;
    };

    if (lightbox) {
      const initDrag = (e, clientX) => {
        // The play/pause control sits inside the track; pressing it is not a swipe.
        if (e.target.closest('.ada-video-pause-btn')) return;
        if (e.target.closest('.lp-pdp__lightbox-image-wrapper') || e.target.closest('#lp-lightbox-track')) {
          isDragging = true;
          touchStartX = clientX;
          const wrapper = section.querySelector('.lp-pdp__lightbox-image-wrapper');
          trackWidth = wrapper ? wrapper.offsetWidth : window.innerWidth;
          setDragPosition(touchStartX);
        }
      };

      lightbox.addEventListener('touchstart', (e) => {
        initDrag(e, e.changedTouches[0].screenX);
      }, { passive: true });

      lightbox.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        setDragPosition(e.changedTouches[0].screenX);
      }, { passive: true });

      lightbox.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        setDragPosition(e.changedTouches[0].screenX);
        handleSwipeEnd();
      }, { passive: true });

      lightbox.addEventListener('mousedown', (e) => {
        initDrag(e, e.clientX);
      });

      lightbox.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        setDragPosition(e.clientX);
      });

      lightbox.addEventListener('mouseup', (e) => {
        if (!isDragging) return;
        setDragPosition(e.clientX);
        handleSwipeEnd();
      });

      lightbox.addEventListener('mouseleave', (e) => {
        if (!isDragging) return;
        setDragPosition(e.clientX);
        handleSwipeEnd();
      });

      // Clicking the video toggles playback. The .ada-video-pause-btn control keeps
      // itself in sync because component-video-pause.js listens for the video's own
      // play/pause events, and it stays the accessible path — the video is aria-hidden
      // and never focusable, so this is a pointer convenience only.
      lightbox.addEventListener('click', (e) => {
        const video = e.target;
        if (video.tagName !== 'VIDEO') return;
        // A swipe ends in a click too; only a press that barely moved is a real click.
        if (Math.abs(touchCurrentX - touchStartX) > 10) return;
        if (video.paused) {
          const playing = video.play();
          if (playing) playing.catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    // ============ Initialize Main Image from Selected Variant ============
    function initializeMainImage() {
      const variantSelect = section.querySelector('[name="id"]');

      // Keep a hero video playing on load; it gives way to an image once a variant is picked.
      if (mainImage && mainImage.tagName === 'VIDEO') return;

      const variantDataEl = section.querySelector('#lp-variant-data');
      
      if (variantSelect && variantDataEl) {
        try {
          const variantData = JSON.parse(variantDataEl.textContent);
          const selectedVariantId = variantSelect.value;
          const selectedVariant = variantData.find(v => v.id == selectedVariantId);
          const url = getMainImageUrlForVariant(selectedVariant);
          if (url) {
            updateMainImage(url);
          }
        } catch(e) {
          console.error('Error parsing variant data', e);
        }
      }
    }

    // ============ Swiper Initialization ============
    /** The swiper only shows on mobile; on desktop it is display:none and its slide maths are unreliable. */
    function isMobileGallery() {
      return window.matchMedia('(max-width: 767px)').matches;
    }

    /**
     * Scrolls the mobile thumbnail strip sideways so `thumbnail` is visible. Avoids
     * scrollIntoView, which also scrolls the page.
     */
    function revealThumbnailInStrip(thumbnail) {
      const strip = section.querySelector('#lp-thumbnail-grid');
      if (!strip || !thumbnail) return;
      const stripRect = strip.getBoundingClientRect();
      const thumbRect = thumbnail.getBoundingClientRect();
      if (thumbRect.left < stripRect.left) {
        strip.scrollLeft += thumbRect.left - stripRect.left;
      } else if (thumbRect.right > stripRect.right) {
        strip.scrollLeft += thumbRect.right - stripRect.right;
      }
    }

    function initializeSwiper() {
      const swiperContainer = section.querySelector('.lp-pdp__swiper');

      if (swiperContainer && window.Swiper) {
        const swiper = new Swiper(swiperContainer, {
          loop: false,
          zoom: {
            maxRatio: 3,
            toggle: true,
          },
          on: {
            // Zooming magnifies the slide's 600px image, so swap in the large copy the first time it zooms.
            zoomChange: (instance, scale, imageEl) => {
              if (scale > 1 && imageEl && imageEl.dataset.zoomSrc && imageEl.src !== imageEl.dataset.zoomSrc) {
                imageEl.src = imageEl.dataset.zoomSrc;
              }
            },
            slideChange: (instance) => {
              // Desktop syncs the main image and thumbnails directly on variant change.
              if (!isMobileGallery()) return;

              // Read the slide by index: Swiper fires slideChange before it moves the
              // .swiper-slide-active class, so that class still points at the old slide.
              const activeSlide = instance.slides[instance.activeIndex];
              const activeImg = activeSlide ? activeSlide.querySelector('img') : null;
              if (activeImg) {
                updateMainImage(activeImg.src, activeImg.alt);
              }
              setActiveThumbnail(instance.activeIndex);
              revealThumbnailInStrip(section.querySelectorAll('.lp-pdp__thumbnail-item')[instance.activeIndex]);
            }
          }
        });
      }
    }

    // ============ Thumbnail Grid Click Handler ============
    /**
     * Marks the thumbnail at `index` as current, visually and for assistive technology.
     * @param {number} index - Thumbnail position; matches the swiper slide index.
     * @returns {void}
     */
    function setActiveThumbnail(index) {
      for (const [i, thumbnail] of section.querySelectorAll('.lp-pdp__thumbnail-item').entries()) {
        thumbnail.classList.toggle('lp-pdp__thumbnail-item--active', i === index);
        thumbnail.setAttribute('aria-current', String(i === index));
      }
    }

    function initializeThumbnailGrid() {
      const thumbnailItems = section.querySelectorAll('.lp-pdp__thumbnail-item');
      
      thumbnailItems.forEach((item, index) => {
        item.addEventListener('click', () => {
          // Mobile: the thumbnail strip drives the swiper instead of the lightbox
          if (isMobileGallery()) {
            section.querySelector('.lp-pdp__swiper')?.swiper?.slideTo(index);
            return;
          }

          const img = item.querySelector('img');
          if (img) {
            setActiveThumbnail(index);

            // Open lightbox with the clicked image
            initializeAllImages();
            // Since thumbnail grid matches swiper index, use the index directly
            if (index < allImages.length) {
              openLightboxAtIndex(index);
            }
          }
        });
      });
    }

    // ============ Desktop: Main Image Click Opens Lightbox ============
    function initializeMainImageLightbox() {
      const mainImageContainer = section.querySelector('#lp-main-image');
      if (mainImageContainer && window.innerWidth >= 768) {
        mainImageContainer.addEventListener('click', (e) => {
          if (e.target.closest('.ada-video-pause-btn')) return;
          initializeAllImages();
          
          // Find the index of the current main image
          const currentSrc = mainImage.src;
          const getPath = (src) => {
            try { return new URL(src, window.location.origin).pathname; }
            catch(e) { return src.split('?')[0]; }
          };
          const currentPath = getPath(currentSrc);
          
          currentImageIndex = allImages.findIndex(media => getPath(media.src) === currentPath);
          if (currentImageIndex === -1) currentImageIndex = 0;
          
          openLightboxAtIndex(currentImageIndex);
        });
      }
    }

    // ============ File Upload Logic ============
    function initializeFileUpload() {
      const fileInput = section.querySelector('#lp-engraving-photo');
      const uploadBox = section.querySelector('.lp-pdp__upload-box');
      const uploadPreview = section.querySelector('.lp-pdp__upload-preview');
      const previewImg = section.querySelector('.lp-pdp__upload-preview-img');
      const deleteBtn = section.querySelector('.lp-pdp__upload-delete');
      const variantDataEl = section.querySelector('#lp-variant-data');
      const variantSelect = section.querySelector('[name="id"]');
      let variantData = [];
      
      if(variantDataEl) {
        try { 
          variantData = JSON.parse(variantDataEl.textContent); 
        } catch(e) {
          console.error('Error parsing variant data', e);
        }
      }

      function showPreview(file) {
        const objectUrl = URL.createObjectURL(file);
        previewImg.src = objectUrl;
        previewImg.onload = () => URL.revokeObjectURL(objectUrl);
        uploadBox.setAttribute('hidden', '');
        uploadPreview.removeAttribute('hidden');
        
        // Update button based on current variant
        if (variantSelect && variantData.length > 0) {
          const currentVariantId = variantSelect.value;
          const currentVariant = variantData.find(v => v.id == currentVariantId);
          if (currentVariant) {
            updateButtonForVariant(currentVariant);
          }
        }
      }

      function clearUpload() {
        previewImg.src = '';
        uploadPreview.setAttribute('hidden', '');
        uploadBox.removeAttribute('hidden');
        const dataTransfer = new DataTransfer();
        fileInput.files = dataTransfer.files;
        
        // Update button based on current variant
        if (variantSelect && variantData.length > 0) {
          const currentVariantId = variantSelect.value;
          const currentVariant = variantData.find(v => v.id == currentVariantId);
          if (currentVariant) {
            updateButtonForVariant(currentVariant);
          }
        }
      }

      if (fileInput) {
        fileInput.addEventListener('change', (e) => {
          if (e.target.files && e.target.files.length > 0) {
            showPreview(e.target.files[0]);
          } else {
            clearUpload();
          }
        });
      }

      if (deleteBtn) {
        deleteBtn.addEventListener('click', clearUpload);
      }
    }

    // ============ Form Submission Logic ============
    function initializeFormSubmission() {
      const productForm = section.querySelector('#lp-product-form');
      const submitBtn = section.querySelector('.lp-pdp__submit-btn');
      const submitText = section.querySelector('.lp-pdp__submit-text');

      if (productForm && submitBtn && submitText) {
        productForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          
          // Only proceed if button is not disabled
          if (submitBtn.disabled) return;

          // Add loading state
          submitBtn.classList.add('lp-pdp__submit-btn--loading');
          submitBtn.disabled = true;

          // Show loading dots
          const originalLabel = submitText.textContent;
          submitText.innerHTML = '<span class="lp-pdp__loading-dots"><span class="lp-pdp__loading-dot"></span><span class="lp-pdp__loading-dot"></span><span class="lp-pdp__loading-dot"></span></span>';
          
          try {
            // Get variant ID
            const variantSelect = section.querySelector('[name="id"]');
            const variantId = variantSelect?.value;
            
            if (!variantId) {
              throw new Error('No variant selected');
            }

            // Build cart item object
            const cartItem = {
              id: Number(variantId),
              quantity: 1,
              properties: {}
            };

            // Shoplift price test: carry the test-assignment property Shoplift
            // wrote into the form so its cart transform can reprice the line.
            const slptInput = productForm.querySelector('input[name="properties[_slpt]"]');
            if (slptInput?.value) {
              cartItem.properties['_slpt'] = slptInput.value;
            }

            // Handle file upload - resize + compress, then store base64 in Photo
            const fileInput = section.querySelector('#lp-engraving-photo');
            if (fileInput && fileInput.files && fileInput.files.length > 0) {
              const file = fileInput.files[0];
              const compressedDataUrl = await compressImageFile(file, 1200, 0.8);
              cartItem.properties['Photo'] = compressedDataUrl;
              cartItem.properties['_Photo Filename'] = file.name;
            }

            // Add other text properties
            const notesInput = section.querySelector('#lp-notes');
            if (notesInput && notesInput.value.trim()) {
              cartItem.properties['Note'] = notesInput.value;
            }

            // Use liquidAjaxCart.add if available
            if (typeof window.liquidAjaxCart?.add === 'function') {
              await new Promise((resolve, reject) => {
                window.liquidAjaxCart.add(
                  { items: [cartItem] },
                  {
                    lastCallback: (state) => {
                      if (state?.responseData?.ok) {
                        resolve();
                      } else {
                        reject(new Error('Cart add failed'));
                      }
                    }
                  }
                );
              });
            } else {
              // Fallback to direct fetch
              const root = window.Shopify?.routes?.root || '/';
              const response = await fetch(`${root}cart/add.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cartItem)
              });

              if (!response.ok) {
                throw new Error('Cart add failed');
              }

              // Update the cart via liquid-ajax-cart if available
              if (typeof window.liquidAjaxCart?.update === 'function') {
                window.liquidAjaxCart.update({}, {});
              }
            }

            // Reset button state and form
            resetButtonState();
            productForm.reset();
            
            // Clear file preview
            const uploadBox = section.querySelector('.lp-pdp__upload-box');
            const uploadPreview = section.querySelector('.lp-pdp__upload-preview');
            if (uploadBox && uploadPreview) {
              uploadPreview.setAttribute('hidden', '');
              uploadBox.removeAttribute('hidden');
            }
          } catch (error) {
            console.error('Error adding to cart:', error);
            submitText.textContent = 'Try again';
            submitBtn.disabled = false;
            submitBtn.classList.remove('lp-pdp__submit-btn--loading');
            
            // Reset text after a delay
            setTimeout(() => {
              submitText.textContent = originalLabel;
            }, 2500);
          }
        });
      }

      const resetButtonState = function () {
        submitBtn.classList.remove('lp-pdp__submit-btn--loading');
        const variantSelect = section.querySelector('[name="id"]');
        const variantDataEl = section.querySelector('#lp-variant-data');
        let variantData = [];
        if (variantDataEl) {
          try {
            variantData = JSON.parse(variantDataEl.textContent);
          } catch (err) {
            variantData = [];
          }
        }
        const currentVariant =
          variantSelect && variantData.length
            ? variantData.find(function (v) {
                return String(v.id) === String(variantSelect.value);
              })
            : null;
        if (currentVariant) {
          updateButtonForVariant(currentVariant);
        } else {
          const fileInput = section.querySelector('#lp-engraving-photo');
          if (fileInput && fileInput.files && fileInput.files.length > 0) {
            submitBtn.disabled = false;
            submitText.textContent = 'Add to Cart';
          } else {
            submitBtn.disabled = true;
            submitBtn.classList.add('lp-pdp__submit-btn--disabled');
            submitText.textContent = 'Image Upload Required';
          }
        }
      };
    }

    // ============ Update Button State for Variant Availability ============
    function updateButtonForVariant(variant) {
      const submitBtn = section.querySelector('#lp-submit-btn');
      const submitText = section.querySelector('.lp-pdp__submit-text');
      const fileInput = section.querySelector('#lp-engraving-photo');
      
      if (!submitBtn || !submitText) return;
      
      const hasFile = fileInput && fileInput.files && fileInput.files.length > 0;
      
      if (variant && !variant.available) {
        // Out of stock - show NOTIFY ME and make clickable, unless the product
        // is tagged `bis-hidden` (no BIS modal rendered) — then it's SOLD OUT.
        const bisHidden = !section.querySelector('#lp-bis-modal');
        submitText.textContent = bisHidden ? 'SOLD OUT' : 'NOTIFY ME';
        submitBtn.disabled = bisHidden;
        submitBtn.classList.toggle('lp-pdp__submit-btn--disabled', bisHidden);
        if (bisHidden) delete submitBtn.dataset.lpBisNotify;
        else submitBtn.dataset.lpBisNotify = 'true';
        submitBtn.type = 'button';
      } else if (hasFile) {
        // In stock with file - show Add to Cart
        submitText.textContent = 'Add to Cart';
        submitBtn.disabled = false;
        submitBtn.classList.remove('lp-pdp__submit-btn--disabled');
        delete submitBtn.dataset.lpBisNotify;
        submitBtn.type = 'submit';
      } else {
        submitText.textContent = 'Image Upload Required';
        submitBtn.disabled = true;
        submitBtn.classList.add('lp-pdp__submit-btn--disabled');
        delete submitBtn.dataset.lpBisNotify;
        submitBtn.type = 'button';
      }
    }

    // ============ Variant Sync Logic ============
    function initializeVariantSync() {
      const variantSelect = section.querySelector('[name="id"]');
      const optionRadios = section.querySelectorAll('.lp-pdp__option-label input[type="radio"]');
      const variantDataEl = section.querySelector('#lp-variant-data');
      let variantData = [];
      
      if(variantDataEl) {
        try { 
          variantData = JSON.parse(variantDataEl.textContent); 
        } catch(e) {
          console.error('Error parsing variant data', e);
        }
      }

      function getVariantOptionValue(variant, optionIndex) {
        if (variant.options && Array.isArray(variant.options)) {
          return variant.options[optionIndex] || null;
        }
        if (optionIndex === 0) return variant.option1 || null;
        if (optionIndex === 1) return variant.option2 || null;
        if (optionIndex === 2) return variant.option3 || null;
        return null;
      }

      // Money formatter — uses active market currency (not a hardcoded $).
      // Prefer section data-currency, then Shopify.currency.active after Markets switch.
      // Forces 'en-US' locale to ensure a dot is used for decimals instead of a comma.
      function formatMoney(cents) {
        const currency =
          section.dataset.currency ||
          window.Shopify?.currency?.active ||
          'USD';
        try {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(Number(cents) / 100);
        } catch (e) {
          const amount = (Number(cents) / 100).toFixed(2).replace(/\.00$/, '');
          return amount + ' ' + currency;
        }
      }

      // Per-variant price: update the on-page price block when the variant changes.
      // The sales-final section owns its own price (sale + compare-at + badge) via its
      // own script, so skip it here to avoid clobbering that markup.
      // Writes go through ShopliftPrice so an active Shoplift price test keeps its
      // adjustment instead of being reset to the Shopify price. The price block is
      // server-rendered with product.price (the lowest variant price), which seeds
      // the adjustment detection.
      function updateMainPrice(variant) {
        if (!variant) return;
        if (section.querySelector('[id^="lp-sf-sale-price-"]')) return;
        const renderedCents = variantData.length ? Math.min(...variantData.map((v) => v.price)) : null;
        section.querySelectorAll('.lp-pdp__price').forEach((el) => {
          window.ShopliftPrice.write(el, variant.price, formatMoney, renderedCents);
        });
      }

      function updateOptionAvailability() {
        if (!variantData.length) return;

        const checkedRadios = Array.from(
          section.querySelectorAll('.lp-pdp__option-label input[type="radio"]:checked')
        );
        const selectedValuesByIndex = [];

        checkedRadios.forEach((radio) => {
          const optionIndex = Number(radio.dataset.index) - 1;
          if (optionIndex >= 0) {
            selectedValuesByIndex[optionIndex] = radio.value;
          }
        });

        optionRadios.forEach((radio) => {
          const optionIndex = Number(radio.dataset.index) - 1;
          const optionValue = radio.value;
          const optionLabel = radio.closest('.lp-pdp__option-label');

          if (!optionLabel || optionIndex < 0) return;

          const matchesSelection = (variant) => {
            if (getVariantOptionValue(variant, optionIndex) !== optionValue) return false;
            return selectedValuesByIndex.every((selectedValue, selectedIndex) => {
              if (!selectedValue || selectedIndex === optionIndex) return true;
              return getVariantOptionValue(variant, selectedIndex) === selectedValue;
            });
          };

          // Does any variant at all use this value with the other selections?
          // If not, the combination doesn't exist (e.g. Gold + Size 10) — hide it.
          const existsAtAll = variantData.some(matchesSelection);
          // Of those, is any in stock? If it exists but is sold out we keep it
          // visible (crossed out) so the NOTIFY ME flow still works.
          const inStock = variantData.some((v) => v.available && matchesSelection(v));

          optionLabel.hidden = !existsAtAll;
          optionLabel.classList.toggle('lp-pdp__option-label--unavailable', existsAtAll && !inStock);
          if (inStock && existsAtAll) {
            radio.removeAttribute('aria-disabled');
          } else {
            radio.setAttribute('aria-disabled', 'true');
          }
          radio.disabled = !existsAtAll;
        });
      }
      
      // Check initial variant on page load
      if (variantSelect && variantData.length > 0) {
        const initialVariantId = variantSelect.value;
        const initialVariant = variantData.find(v => v.id == initialVariantId);
        if (initialVariant) {
          updateButtonForVariant(initialVariant);
          updateMainPrice(initialVariant);
        }
      }
      updateOptionAvailability();

      // When the picked value makes the current combination non-existent (e.g.
      // Gold + Size 10 with no such variant), steer the OTHER option groups to
      // the nearest real variant so the selection always resolves to one that
      // exists — never leaving a stale variant id behind.
      function reconcileToValidVariant(changedRadio) {
        if (!variantData.length) return;
        const changedIndex = Number(changedRadio.dataset.index) - 1;
        if (changedIndex < 0) return;

        const selectedOptions = Array.from(
          section.querySelectorAll('.lp-pdp__option-label input[type="radio"]:checked')
        ).map(r => r.value);

        const alreadyValid = variantData.some(v =>
          selectedOptions.every((val, i) => getVariantOptionValue(v, i) === val)
        );
        if (alreadyValid) return;

        const candidate = variantData.find(v =>
          getVariantOptionValue(v, changedIndex) === changedRadio.value
        );
        if (!candidate) return;

        const groups = Array.from(section.querySelectorAll('.lp-pdp__option-group'));
        groups.forEach((group, index) => {
          if (index === changedIndex) return;
          const target = getVariantOptionValue(candidate, index);
          if (!target) return;
          const targetRadio = group.querySelector(`input[type="radio"][value="${target.replace(/"/g, '\\"')}"]`);
          if (targetRadio && !targetRadio.checked) {
            targetRadio.checked = true;
            const selectedValueEl = section.querySelector(`[data-option-selected="${targetRadio.dataset.index}"]`);
            if (selectedValueEl) selectedValueEl.textContent = targetRadio.value;
          }
        });
      }

      optionRadios.forEach(radio => {
        radio.addEventListener('change', () => {
          // Update label text
          const labelEl = section.querySelector(`[data-option-selected="${radio.dataset.index}"]`);
          if(labelEl) labelEl.textContent = radio.value;
          // Auto-correct impossible combinations before resolving the variant.
          reconcileToValidVariant(radio);
          updateOptionAvailability();

          // Find matching variant
          if (variantData.length > 0) {
            const selectedOptions = Array.from(section.querySelectorAll('.lp-pdp__option-label input[type="radio"]:checked')).map(r => r.value);
            const matchedVariant = variantData.find(v => {
              if (v.options && Array.isArray(v.options)) {
                return v.options.every((opt, i) => opt === selectedOptions[i]);
              }
              const opt1 = selectedOptions[0] || null;
              const opt2 = selectedOptions[1] || null;
              const opt3 = selectedOptions[2] || null;
              return v.option1 === opt1 && v.option2 === opt2 && v.option3 === opt3;
            });

            if (matchedVariant && variantSelect) {
              variantSelect.value = matchedVariant.id;
              
              // Update button for variant availability
              updateButtonForVariant(matchedVariant);

              // Update the on-page price for the selected variant
              updateMainPrice(matchedVariant);

              const newImageSrc = getMainImageUrlForVariant(matchedVariant);

              if (newImageSrc) {
                const slideIndex = getSlideIndexForMedia(getVariantMediaId(matchedVariant));
                const slideImg =
                  slideIndex !== -1
                    ? section.querySelectorAll('.lp-pdp__swiper-slide')[slideIndex].querySelector('img')
                    : null;

                updateMainImage(newImageSrc, slideImg ? slideImg.alt : '');

                if (slideIndex !== -1) {
                  if (isMobileGallery()) {
                    const swiperContainer = section.querySelector('.lp-pdp__swiper');
                    if (swiperContainer && swiperContainer.swiper) {
                      swiperContainer.swiper.slideTo(slideIndex);
                    }
                  } else {
                    // Desktop: the hidden swiper would scroll the page and overwrite the
                    // main image, so mark the thumbnail directly.
                    setActiveThumbnail(slideIndex);
                  }
                }
              }

              // Always announce the change — previously variants without an image never fired it.
              variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
            } else if (variantSelect) {
              // No real variant for this combination (should not happen after
              // reconcile, but guard so a stale id can never be submitted).
              variantSelect.value = '';
              updateButtonForVariant(null);
              variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      });
    }

    // ============ Photo Guidelines Modal ============
    const guidelinesTrigger = section.querySelector('#lp-guidelines-trigger');
    const guidelinesModal = section.querySelector('#lp-guidelines-modal');
    const guidelinesClose = section.querySelector('#lp-guidelines-close');
    const guidelinesOverlay = section.querySelector('#lp-guidelines-overlay');

    if (guidelinesTrigger && guidelinesModal) {
      guidelinesTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        guidelinesModal.classList.add('lp-pdp__guidelines-modal--active');
      });
    }

    if (guidelinesClose && guidelinesModal) {
      guidelinesClose.addEventListener('click', () => {
        guidelinesModal.classList.remove('lp-pdp__guidelines-modal--active');
      });
    }

    if (guidelinesOverlay && guidelinesModal) {
      guidelinesOverlay.addEventListener('click', () => {
        guidelinesModal.classList.remove('lp-pdp__guidelines-modal--active');
      });
    }

    // ============ Redo Modal ============
    // Follow the trigger's aria-controls: some forms scope the modal id to the section, others don't.
    const redoTrigger = section.querySelector('.lp-pdp__redo-info-btn');
    const redoModalId = redoTrigger?.getAttribute('aria-controls');
    const redoModal = redoModalId ? section.querySelector(`#${CSS.escape(redoModalId)}`) : null;
    const redoClose = section.querySelector('#lp-redo-close');
    const redoOverlay = section.querySelector('#lp-redo-overlay');

    if (redoTrigger && redoModal) {
      redoTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        redoModal.classList.add('lp-pdp__guidelines-modal--active');
      });
    }

    if (redoClose && redoModal) {
      redoClose.addEventListener('click', () => {
        redoModal.classList.remove('lp-pdp__guidelines-modal--active');
      });
    }

    if (redoOverlay && redoModal) {
      redoOverlay.addEventListener('click', () => {
        redoModal.classList.remove('lp-pdp__guidelines-modal--active');
      });
    }

    // ============ Initialize Everything ============
    prefetchMainImagesForSwatches();
    initializeMainImage();
    initializeSwiper();
    initializeThumbnailGrid();
    initializeMainImageLightbox();
    initializeFileUpload();
    initializeFormSubmission();
    initializeVariantSync();
  }

  document.addEventListener('DOMContentLoaded', initLpPdpGallery);
  /* Fired by <lih-product-tabs> once a fetched product section is in the DOM. */
  document.addEventListener('lih:pdp-loaded', initLpPdpGallery);
})();
